import { useEffect, useRef, useState } from "react";
import { BiBlock, BiCopy, BiLockOpen, BiMessageAltError, BiTrash, BiChevronDown } from "react-icons/bi";
import { FaReply } from "react-icons/fa";
import { GoMute, GoUnmute } from "react-icons/go";
import { LuLoader } from "react-icons/lu";
import { PiPaperPlaneTilt } from "react-icons/pi";
import Swal from "sweetalert2";
import Api from "../../../service/http";
import { useSocket } from "../../../service/SocketProvider";

const MessageAction = ({ message, isSender, setReply, theme }) => {
  const [loading, setLoading] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const actionRef = useRef(null);

  const { emitLocal } = useSocket();

  const onClickAction = async (action) => {
    if (loading) return; // Prevent multiple clicks
    setLoading(true);
    var res = null;
    var params = {};
    var isWebFetchMessage = false;
    var isWebFetchAllChats = false;

    switch (action) {
      case 'reply':
        setReply(message);
        break;
      case 'copy':
        navigator.clipboard.writeText(message?.message || "");
        break;
      case 'delete':
        params = { type: "delete" };
        isWebFetchMessage = true;
        break;
      case 'mute':
        params = { type: message?.is_mute ? "unmute" : "mute" };
        isWebFetchAllChats = true;
        break;
      case 'block':
        params = { type: message?.is_block ? "unblock" : "block" };
        isWebFetchAllChats = true;
        break;
      case 'report':
        const result = await Swal.fire({
          title: 'Are you sure you want to report this message?',
          text: 'This action will be reviewed by the admin.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Report',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6'
        });

        if (result.isConfirmed) {
          params = { type: "report" };
          isWebFetchMessage = true;
        }

        break;
      case 'unsend':
        params = { type: "unsend" };
        isWebFetchMessage = true;
        break;
      default:
        console.log("Unknown action");
    }


    // The server pushes the change to whoever should see it; the local emit only
    // updates this tab right away from our own response.
    try {
      if (isWebFetchMessage) {
        res = await Api.post(`/web/chat/${message?.id}/action`, params);
        if (res?.status === 200 && res?.data?.id) {
          emitLocal({
            type: "WebFetchMessage",
            chat_id: res.data.chat_id,
            message: res.data,
            isScroll: false,
          });
        } else {
          alert(res?.data?.message || "Action failed. Please try again.");
        }
      }

      if (isWebFetchAllChats) {
        res = await Api.post(`/web/chat/${message?.id}/action`, params);
        if (res?.status === 200) {
          emitLocal({
            type: "WebFetchChat",
            chat_id: message?.chat_id,
            message: { chat_id: message?.chat_id },
            isScroll: false,
          });
        } else {
          alert(res?.data?.message || "Action failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("Message action failed:", error);
      alert("Action failed. Please check your connection and try again.");
    }

    setShowActions(false);
    setLoading(false);
  }

  const buttons = [
    { id: 'reply', icon: <FaReply />, label: "Reply", isUnderLined: true, isDanger: false, showFor: 'both' },
    { id: 'copy', icon: <BiCopy />, label: "Copy", isUnderLined: true, isDanger: false, showFor: 'both' },
    { id: 'delete', icon: <BiTrash />, label: "Delete for me", isUnderLined: true, isDanger: false, showFor: 'both' },
    { id: 'mute', icon: message?.is_mute ? <GoUnmute /> : <GoMute />, label: message?.is_mute ? "Unmute" : "Mute", isUnderLined: true, isDanger: false, showFor: 'receiver' },
    { id: 'block', icon: message?.is_block ? <BiLockOpen color="red" /> : <BiBlock color="red" />, label: message?.is_block ? "Unblock" : "Block", isUnderLined: true, isDanger: true, showFor: 'receiver' },
    { id: 'report', icon: <BiMessageAltError color="red" />, label: "Report", isUnderLined: false, isDanger: true, showFor: 'receiver' },
    { id: 'unsend', icon: <PiPaperPlaneTilt />, label: "Unsend", isUnderLined: false, isDanger: false, showFor: 'sender' },
  ]

      // Close modal when clicking outside
      useEffect(() => {
        const handleClickOutside = (event) => {
          if (actionRef.current && !actionRef.current.contains(event.target)) {
            setShowActions(false);
          }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }, []);

    return (
      <div className="relative" ref={actionRef}>
            <button
                onClick={() => setShowActions(!showActions)}
                className="p-0.5 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors flex items-center justify-center"
            >
              <BiChevronDown size={18} className="opacity-70" />
            </button>

            {/* Modal */}
            {showActions && 
          <div className={`absolute top-full mt-1 bg-white dark:bg-gray-800 shadow-2xl border border-gray-100 dark:border-gray-700 rounded-xl p-1 z-50 w-max right-0 animate-in fade-in zoom-in duration-200 origin-top-right`}
              >
                {buttons?.map((button, index) => {
                  const isVisible = (isSender && (button?.showFor === 'both' || button?.showFor === 'sender')) || 
                                    (!isSender && (button?.showFor === 'both' || button?.showFor === 'receiver'));
                  
                  if (!isVisible) return null;

                  return (
                    <div key={button.id}>
                      <button
                        onClick={() => onClickAction(button?.id)}
                        disabled={loading}
                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-sm rounded-lg transition-colors ${
                          button?.isDanger 
                            ? "text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10" 
                            : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                        }`}
                      >
                        <span className="text-base">
                          {loading ? <LuLoader className="animate-spin" /> : button?.icon}
                        </span>
                        <span className="font-medium text-xs">{button?.label}</span>
                      </button>
                      {button?.isUnderLined && index !== buttons.length - 1 && (
                        <div className="my-0.5 border-b border-gray-100 dark:border-gray-700/50" />
                      )}
                    </div>
                  );
                })}
              </div>
            }
          </div>
    )
}

export default MessageAction;